import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import { useSettings } from "@/lib/site";

const LOGO = "/verde2050-logo-silver.png";

export const NAV = [
    { label: "Home", to: "/" },
    { label: "Projects", to: "/projects" },
    { label: "What We Do", to: "/what-we-do" },
    { label: "Land Intelligence", to: "/land-intelligence" },
    { label: "Sell Your Land", to: "/sell-your-land" },
    { label: "Investor Circle", to: "/investor-circle" },
    { label: "News", to: "/news" },
    { label: "Contact", to: "/contact" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function GroupHeader({ alwaysDark = false }) {
    const [scrolled, setScrolled] = useState(alwaysDark);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const settings = useSettings();

    useEffect(() => {
        if (alwaysDark) return;
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [alwaysDark]);

    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const solid = alwaysDark || scrolled || open;

    return (
        <>
            <header data-testid="group-header"
                    className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${solid ? "bg-forest/95 backdrop-blur-xl border-cream/10" : "bg-transparent border-transparent"}`}>
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-6">
                    <Link to="/" className="flex items-center" data-testid="group-logo" aria-label="Verde 2050 — home">
                        <img src={LOGO} alt="Verde 2050" className="h-[20px] md:h-[24px] w-auto object-contain" />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-5 xl:gap-8" aria-label="Main">
                        {NAV.map((n) => (
                            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${slug(n.label)}`}
                                     className={({ isActive }) => `relative text-[13px] tracking-wide transition-colors ${isActive ? "text-cream" : "text-cream/70 hover:text-cream"} after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-sage after:transition-[width] after:duration-300 ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`}>
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link to="/contact" data-testid="nav-work-with-us"
                              className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-cream text-forest hover:bg-sage px-5 py-2.5 text-[13px] font-medium transition-colors">
                            Work With Us
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="menu-toggle"
                                aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
                                className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-cream">
                            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </header>

            <div data-testid="menu-panel" aria-hidden={!open}
                 className={`lg:hidden fixed inset-0 z-40 bg-forest transition-opacity duration-500 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
                <div className="flex flex-col h-full pt-28 pb-10 px-6 overflow-y-auto">
                    <nav className="flex flex-col">
                        {NAV.map((n, i) => (
                            <Link key={n.to} to={n.to} data-testid={`menu-${slug(n.label)}`}
                                  style={{ transitionDelay: open ? `${i * 50}ms` : "0ms" }}
                                  className={`group flex items-center justify-between border-b border-cream/10 py-5 font-serif text-4xl transition-[opacity,transform] duration-500 ${open ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"} ${location.pathname === n.to ? "text-sage" : "text-cream"}`}>
                                {n.label}
                                <ArrowUpRight className="h-5 w-5 opacity-50" />
                            </Link>
                        ))}
                    </nav>
                    <Link to="/contact" data-testid="menu-work-with-us"
                          className="mt-10 inline-flex items-center justify-center gap-3 rounded-full bg-cream text-forest px-7 py-4 text-sm font-medium">
                        Work With Us <ArrowRight className="h-4 w-4" />
                    </Link>
                    <div className="mt-auto pt-10 mono-label text-cream/50">
                        <p>Verde 2050 — Built for Tomorrow</p>
                        <p className="mt-1">{settings.contact_email}</p>
                    </div>
                </div>
            </div>
        </>
    );
}
